(() => {
  const INDICATOR_SELECTOR = '[data-gift-progress]';
  const EVENTS = ['theme:cart:loaded', 'theme:cart:update', 'theme:cart:added', 'theme:cart:refresh'];

  const formatMoney = (amount, includeCode = false) => {
    const cents = Math.max(0, Math.round(Number(amount) || 0));
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      let formatted = window.theme.formatMoney(cents, window.theme.moneyFormat);
      if (includeCode && window.theme && window.theme.current_iso_code) {
        formatted = `${formatted} ${window.theme.current_iso_code}`;
      }

      if (/<[^>]+>/.test(formatted)) {
        const temp = document.createElement('div');
        temp.innerHTML = formatted;
        formatted = temp.textContent || temp.innerText || '';
        formatted = formatted.trim();
      }

      return formatted;
    }

    const dollars = (cents / 100).toFixed(2);
    if (includeCode && window.theme && window.theme.current_iso_code) {
      return `${dollars} ${window.theme.current_iso_code}`;
    }
    return dollars;
  };

  const parseTiers = (element) => {
    const tierNodes = element.querySelectorAll('[data-gift-tier]');
    if (!tierNodes.length) return [];

    return Array.from(tierNodes)
      .map((node) => ({
        threshold: Number(node.dataset.threshold) || 0,
        label: node.dataset.label || '',
        message: node.dataset.message || '',
        unlockedMessage: node.dataset.unlockedMessage || ''
      }))
      .filter((tier) => tier.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const buildMilestones = (context) => {
    const { tiers, milestonesEl, labelsEl, maxThreshold } = context;
    milestonesEl.innerHTML = '';
    labelsEl.innerHTML = '';

    context.milestoneNodes = [];
    context.labelNodes = [];

    tiers.forEach((tier) => {
      const percent = maxThreshold ? clamp((tier.threshold / maxThreshold) * 100, 0, 100) : 0;

      const milestone = document.createElement('div');
      milestone.className = 'gift-progress__milestone gift-progress__milestone--upcoming';
      milestone.style.left = `${percent}%`;
      milestone.dataset.threshold = String(tier.threshold);
      milestonesEl.appendChild(milestone);

      const label = document.createElement('span');
      label.className = 'gift-progress__label';
      label.style.left = `${percent}%`;
      label.textContent = tier.label || formatMoney(tier.threshold, false);
      labelsEl.appendChild(label);

      context.milestoneNodes.push(milestone);
      context.labelNodes.push(label);
    });
  };

  const getSubtotalFromDom = (element) => {
    const drawer = element.closest('[data-cart-drawer]') || document;
    const totalEl = drawer.querySelector('[data-cart-total]');
    if (!totalEl) return Number(element.dataset.giftProgressSubtotal) || 0;
    const raw = totalEl.getAttribute('data-cart-total');
    return Number(raw) || 0;
  };

  const updateStates = (context, subtotal) => {
    const {
      tiers,
      fillEl,
      messageEl,
      statusEl,
      milestoneNodes,
      labelNodes,
      maxThreshold,
      includeCurrencyCode
    } = context;

    if (!tiers.length || !maxThreshold) {
      context.element.hidden = true;
      return;
    }

    context.element.hidden = false;
    const constrainedSubtotal = Math.max(0, subtotal);
    const percent = clamp((constrainedSubtotal / maxThreshold) * 100, 0, 100);
    fillEl.style.width = `${percent}%`;

    const nextTier = tiers.find((tier) => constrainedSubtotal < tier.threshold);
    const activeTier = nextTier || tiers[tiers.length - 1];

    milestoneNodes.forEach((node, index) => {
      const tier = tiers[index];
      node.classList.remove(
        'gift-progress__milestone--completed',
        'gift-progress__milestone--active',
        'gift-progress__milestone--upcoming'
      );

      labelNodes[index].classList.remove('gift-progress__label--active');

      if (constrainedSubtotal >= tier.threshold) {
        node.classList.add('gift-progress__milestone--completed');
      } else if (nextTier && tier.threshold === nextTier.threshold) {
        node.classList.add('gift-progress__milestone--active');
        labelNodes[index].classList.add('gift-progress__label--active');
      } else if (!nextTier && tier.threshold === tiers[tiers.length - 1].threshold) {
        node.classList.add('gift-progress__milestone--active');
        labelNodes[index].classList.add('gift-progress__label--active');
      } else {
        node.classList.add('gift-progress__milestone--upcoming');
      }
    });

    let messageText = '';

    if (nextTier) {
      const remaining = Math.max(0, nextTier.threshold - constrainedSubtotal);
      const formatted = formatMoney(remaining, includeCurrencyCode);
      const template = nextTier.message || `You’re {$amount} away from your next gift`;
      messageText = template.includes('{$amount}') ? template.replace('{$amount}', formatted) : `${template} ${formatted}`;
    } else {
      const finalTier = tiers[tiers.length - 1];
      messageText = finalTier.unlockedMessage || 'All gifts unlocked!';
    }

    messageEl.textContent = messageText;

    if (statusEl) {
      const statusMessage = nextTier
        ? `Cart total ${formatMoney(constrainedSubtotal, includeCurrencyCode)}. ${messageText}`
        : `Cart total ${formatMoney(constrainedSubtotal, includeCurrencyCode)}. All gifts unlocked.`;
      statusEl.textContent = statusMessage.trim();
    }
  };

  const attachObservers = (context) => {
    const refresh = () => {
      const subtotal = getSubtotalFromDom(context.element);
      updateStates(context, subtotal);
    };

    EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, refresh);
    });

    const drawerRoot = context.element.closest('[data-cart-drawer]');
    const subtotalObserverTarget = drawerRoot ? drawerRoot.querySelector('[data-cart-total]') : null;
    if (subtotalObserverTarget) {
      const observer = new MutationObserver(() => refresh());
      observer.observe(subtotalObserverTarget, { attributes: true, attributeFilter: ['data-cart-total'] });
      context.observer = observer;
    }

    window.addEventListener('resize', () => {
      // No recalculation needed for positions since they are percent-based,
      // but forcing a style recalculation keeps Safari in sync during rotation.
      context.milestoneNodes.forEach((node) => (node.style.transform = node.style.transform));
    });

    refresh();
  };

  const initIndicator = (element) => {
    const tiers = parseTiers(element);
    if (!tiers.length) {
      element.hidden = true;
      return;
    }

    const maxThresholdData = Number(element.dataset.giftProgressMax) || 0;
    const maxThreshold = maxThresholdData || tiers[tiers.length - 1].threshold;
    const includeCurrencyCode = element.dataset.giftProgressCurrencyWithCode === 'true' || element.dataset.giftProgressCurrencyWithCode === '1';

    const context = {
      element,
      tiers,
      maxThreshold,
      includeCurrencyCode,
      fillEl: element.querySelector('[data-gift-progress-fill]'),
      messageEl: element.querySelector('[data-gift-progress-message]'),
      statusEl: element.querySelector('[data-gift-progress-status]'),
      milestonesEl: element.querySelector('[data-gift-progress-milestones]'),
      labelsEl: element.querySelector('[data-gift-progress-labels]'),
      milestoneNodes: [],
      labelNodes: []
    };

    if (!context.fillEl || !context.messageEl || !context.milestonesEl || !context.labelsEl) {
      element.hidden = true;
      return;
    }

    context.element.giftProgressContext = context;

    buildMilestones(context);

    const initialSubtotal = Number(element.dataset.giftProgressSubtotal) || getSubtotalFromDom(element);
    updateStates(context, initialSubtotal);
    attachObservers(context);
  };

  const boot = () => {
    document.querySelectorAll(INDICATOR_SELECTOR).forEach((element) => {
      if (!element.giftProgressContext) {
        initIndicator(element);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('theme:cart:loaded', boot);
  document.addEventListener('theme:cart:open', boot);
})();
